import { useEffect, useRef, useSyncExternalStore } from "react";
import { SOCKET_MESSAGE_TYPES } from "../../other/constants/OtherKeys.jsx";
import { WEBSOCKET_ACTION_KEY } from "../../env.jsx";

const ACTIVITY_INTERVAL = 5000;
const BROADCAST_INTERVAL = 10000;

const useEditorLiveUpdate = (activeEditors, user, swivuWebSocket, boardSessionId) => {
  const otherUserEditorActivityRef = useRef([]);
  const isSocketConnected = useSyncExternalStore(
    swivuWebSocket ? swivuWebSocket.renderSubscribe.bind(swivuWebSocket): () => {},
    swivuWebSocket ? swivuWebSocket.renderSnapShot.bind(swivuWebSocket): () => {}
  );

  const loadActiveUsers = () => {
    activeEditors.forEach(({ type, recordIdObj, editorRef }) => {
      // editorRef is assigned later in the object when the onShow function is called from the SwivuModal component
      if (!recordIdObj?.isPhantom && editorRef?.isMainEditorBase) {
        // Filter the active users to include only those whose editor ID matches the current record ID and exclude the current user from different sessions
        const activeUsers = otherUserEditorActivityRef.current.filter((editor) => editor.type === type && editor.id === recordIdObj.id && editor.userId !== user.id);
        editorRef.onActiveUsers(activeUsers)
      }
    })
  }

  const broadCastUserEditors = () => {
    if (isSocketConnected) {
      const editorsToBroadcast = activeEditors.reduce((previousEditors, { type, recordIdObj, editorRef }) => {
        if (editorRef?.isMainEditorBase && recordIdObj && !recordIdObj.isPhantom) {
          previousEditors.push({
            id: recordIdObj.id,
            userId: user.id,
            type
          })
        }
        return previousEditors;
      }, []);
      if (editorsToBroadcast.length) {
        swivuWebSocket.sendMessage({
          [WEBSOCKET_ACTION_KEY]: SOCKET_MESSAGE_TYPES.ACTIVITY,
          board_session_id: boardSessionId,
          editors: editorsToBroadcast
        })
      }
    }
  }

  const handleOtherUserActivity = ({ data }) => {
    const { [WEBSOCKET_ACTION_KEY]: action, board_session_id, editors } = JSON.parse(data);
    if (action === SOCKET_MESSAGE_TYPES.ACTIVITY && board_session_id !== boardSessionId) {
      const lastActiveTime = new Date().getTime();
      editors.forEach((editor) => {
        const isExistingEditor = otherUserEditorActivityRef.current.find((record) => record.type === editor.type && editor.id === record.id && editor.userId === record.userId);
        if (isExistingEditor) {
          isExistingEditor.lastActiveTime = lastActiveTime;
        } else {
          otherUserEditorActivityRef.current.push({ ...editor, lastActiveTime })
        }
      })
    }
  }

  const cleanUpInActiveUsers = () => {
    const currentTime = new Date().getTime();
    otherUserEditorActivityRef.current = otherUserEditorActivityRef.current.filter((editor) => currentTime - editor.lastActiveTime < BROADCAST_INTERVAL)
  }

  useEffect(() => {
    if (isSocketConnected) {
      // Not removing listener because it is automatically destroyed when a socket connection is closed.
      swivuWebSocket.attachListenerToSocket("message", handleOtherUserActivity);
    }
  }, [isSocketConnected]);

  useEffect(() => {
    if (activeEditors.length) {
      broadCastUserEditors();
      const activityInterval = setInterval(loadActiveUsers, ACTIVITY_INTERVAL);
      const cleanUpInterval = setInterval(() => {
        cleanUpInActiveUsers();
        broadCastUserEditors();
      }, BROADCAST_INTERVAL);
      return () => {
        clearInterval(activityInterval);
        clearInterval(cleanUpInterval);
      }
    }
  }, [activeEditors]);
};

export default useEditorLiveUpdate;
